import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import { Field, FieldProps } from '../Field/Field';
import { colors, s32, s8, space } from '@ardoq/design-tokens';
import { ICON_SELECTOR } from '@ardoq/icons';
import { GroupFieldLayout } from './types';
import { text2 } from '@ardoq/typography';
import { CommonFieldProps } from '../Field/types';
import type { SpaceVariant } from '@ardoq/layout';

const getGroupFieldLayout = (props: {
  $groupFieldLayout: GroupFieldLayout;
  $gap?: Exclude<SpaceVariant, 'none'>;
}) => {
  const groupFieldLayout = parseInt(`${props.$groupFieldLayout}`, 10);
  if (groupFieldLayout === GroupFieldLayout.HORIZONTAL) {
    return css`
      flex-direction: row;
      gap: ${props.$gap ? space[props.$gap] : s32};
    `;
  }
  if (groupFieldLayout === GroupFieldLayout.JUSTIFIED) {
    return css`
      flex-direction: row;
      justify-content: space-between;
      ${props.$gap &&
      css`
        gap: ${space[props.$gap]};
      `}
    `;
  }

  // VERTICAL
  return css`
    flex-direction: column;
    align-items: baseline;
    gap: ${props.$gap ? space[props.$gap] : s8};
  `;
};

type GroupFieldItemProps = {
  $isDisabled?: boolean;
  $isActive?: boolean;
  $hasError?: boolean;
  $hasWarning?: boolean;
};

const getIconColor = ({
  $hasError,
  $hasWarning,
  $isDisabled,
  $isActive,
}: GroupFieldItemProps) => {
  if ($isDisabled) return colors.iconActionDisabled;
  if ($hasError) return colors.iconError;
  if ($hasWarning) return colors.iconWarning;
  if ($isActive) return colors.iconActionActive;
  return colors.iconAction;
};

export const GroupFieldItem = styled.div<GroupFieldItemProps>`
  ${text2};
  align-items: center;
  display: flex;
  margin: 0;
  cursor: pointer;
  color: ${colors.textDefault};

  /* used in order to place <CheckboxFocusBox /> or <RadioFocusBox /> */
  position: relative;

  & > ${ICON_SELECTOR} {
    margin-right: ${s8};
    color: ${getIconColor};
  }

  & .radio-icon-wrapper {
    /* this is just a quick fix for the radio icon that is wrapped with one more div
       we should not break encapsulation and rely on nesting in our styling */
    color: ${getIconColor};
  }

  &:focus-visible {
    outline: 0;
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      color: ${colors.textDisabled};
    `}
`;

const GroupFieldWrapper = styled.div<{
  $groupFieldLayout: GroupFieldLayout;
  $gap?: Exclude<SpaceVariant, 'none'>;
}>`
  display: flex;
  ${getGroupFieldLayout};
`;

export type GroupFieldProps = FieldProps &
  Pick<CommonFieldProps, 'hasError' | 'hasWarning'> & {
    children?: ReactNode;
    groupFieldLayout?: GroupFieldLayout;
    /**
     * Overrides the default gap between group items. Applies to `HORIZONTAL`,
     * `VERTICAL`, and `JUSTIFIED` layouts. When omitted, the layout's default
     * gap is used (32px horizontal, 8px vertical). `'none'` is excluded
     * because a zero gap silently collapses items onto each other; omit the
     * prop instead.
     */
    gap?: Exclude<SpaceVariant, 'none'>;
  };

export const GroupField = ({
  children,
  groupFieldLayout = GroupFieldLayout.VERTICAL,
  gap,
  ...rest
}: GroupFieldProps) => (
  <Field {...rest}>
    <GroupFieldWrapper $groupFieldLayout={groupFieldLayout} $gap={gap}>
      {children}
    </GroupFieldWrapper>
  </Field>
);

export const validGroupFieldProps: Array<keyof GroupFieldProps> = [
  'groupFieldLayout',
  'gap',
  'errorMessage',
  'hintMessage',
  'label',
  'isDisabled',
  'warningMessage',
  'helperText',
  'hasError',
  'hasWarning',
  'popoverHelpContent',
  'formSize',
];
